import { type ReactNode, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { LottiePlayer, Button } from '@repo/design-system-kit';
import { useSegment } from '@repo/poly-hooks/segment';
import styles from './error.module.css';

interface ErrorProps {
  errorType: string;
  onRetry?: () => void;
}

export function Error({ errorType, onRetry }: ErrorProps): ReactNode {
  const segment = useSegment();
  const isExpiredOrInvalid = errorType === 'mandate_expired' || errorType === 'mandate_invalid';

  useEffect(() => {
    const isExpired = errorType === 'mandate_expired';
    segment.track('public-page_sdd-payment-link_failed', { expired: isExpired });
  }, [segment, errorType]);

  return (
    <div className={styles.container} data-test-sdd-payment-link-error>
      <LottiePlayer
        autoplay
        className={`${styles.lottie} mb-32`}
        data-testid="payment-link-error-illustration"
        loop
        path="/lotties/satellite.json"
      />

      {isExpiredOrInvalid ? (
        <div className={styles.textContainer}>
          <h1 className="title-1 mb-8" data-test-payment-link-expired-title>
            <FormattedMessage id="error-states.payment-link-expired.title" />
          </h1>
          <p className="body-1" data-test-payment-link-expired-subtitle>
            <FormattedMessage id="error-states.payment-link-expired.legend" />
          </p>
        </div>
      ) : (
        <>
          <div className={styles.textContainer}>
            <h1 className="title-1 mb-8" data-test-payment-link-error-title>
              <FormattedMessage id="error-states.sdd-collections.title" />
            </h1>
            <p className="body-1 mb-32" data-test-payment-link-error-subtitle>
              <FormattedMessage id="error-states.sdd-collections.legend" />
            </p>
          </div>
          {onRetry ? (
            <Button
              data-test-payment-link-error-button
              onClick={onRetry}
              type="button"
              variant="primary"
            >
              <FormattedMessage id="btn.retry" />
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}
